import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { BeforeAfterGallery } from '../components/BeforeAfterGallery';
import { ShieldCheck, HeartHandshake, Hammer, Clock, Phone, ArrowRight, Bath, Droplets, Grid3x3, Accessibility, Lightbulb, BoxIcon } from 'lucide-react';
export function HomePage() {
  return <div className="flex flex-col min-h-screen" data-id="element-473">
      {/* Hero Section */}
      <section className="relative pt-40 pb-20 lg:pt-56 lg:pb-32 overflow-hidden" data-id="element-474">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-475">
          <img src="https://cdn.magicpatterns.com/uploads/q7xZuEXRB38pU4N9M5kTmq/hero2.jpg" alt="Beautiful modern bathroom" className="w-full h-full object-cover" data-id="element-476" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/70 to-white/30 md:to-transparent" data-id="element-477"></div>
        </div>

        <div className="container relative z-10 mx-auto px-4 md:px-6" data-id="element-478">
          <div className="max-w-2xl animate-in slide-in-from-left-10 duration-700 fade-in" data-id="element-479">
            <div className="inline-flex items-center rounded-full border border-[#8BA888]/30 bg-[#8BA888]/10 px-3 py-1 text-sm font-medium text-[#1E3A5F] mb-6" data-id="element-480">
              <span className="flex h-2 w-2 rounded-full bg-[#8BA888] mr-2" data-id="element-481"></span>
              Trusted Local Bathroom Remodelers
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6" data-id="element-482">
              Professional Bathroom Remodeling in{' '}
              <span className="text-[#8BA888]" data-id="element-483">Northern Indiana</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-484">
              Transform your bathroom with high-quality craftsmanship, honest
              pricing, and a stress-free remodeling process. Lucky Duck Bathroom
              Company delivers custom bathroom renovations built to
              last—designed around your home, your needs, and your budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-485">
              <Link to="/contact" data-id="element-486">
                <Button size="lg" className="w-full sm:w-auto shadow-lg shadow-[#8BA888]/20" data-id="element-487">
                  Request Free Consultation
                </Button>
              </Link>
              <Button variant="outline" size="lg" className="w-full sm:w-auto bg-white/50 backdrop-blur-sm" data-id="element-488">
                <Phone className="mr-2 h-5 w-5" data-id="element-489" />
                Call (574) 914-1522
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-20 bg-white" data-id="element-490">
        <div className="container mx-auto px-4 md:px-6" data-id="element-491">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-492">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-493">
              Why Choose Lucky Duck?
            </h2>
            <p className="text-lg text-gray-600" data-id="element-494">
              We believe remodeling shouldn't be a headache. Here's how we make
              it different.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-495">
            {[{
            icon: Hammer,
            title: 'Quality Craftsmanship',
            desc: "We don't cut corners. Every tile, fixture, and finish is installed with precision and care."
          }, {
            icon: HeartHandshake,
            title: 'No-Pressure Service',
            desc: 'Honest advice and transparent pricing without the pushy sales tactics.'
          }, {
            icon: ShieldCheck,
            title: 'Clean & Respectful',
            desc: 'We protect your home, keep the job site tidy, and treat your space with respect.'
          }, {
            icon: Clock,
            title: 'Built to Last',
            desc: 'Durable materials and proper installation methods ensure your bathroom stands the test of time.'
          }].map((item, index) => <Card key={index} className="p-6 text-center hover:-translate-y-1 transition-transform duration-300 border-t-4 border-t-[#8BA888]" data-id="element-496">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#8BA888]/10 text-[#8BA888]" data-id="element-497">
                  <item.icon className="h-7 w-7" data-id="element-498" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-499">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-500">
                  {item.desc}
                </p>
              </Card>)}
          </div>

          {/* The Right Way Section */}
          <div className="mt-16 max-w-4xl mx-auto" data-id="element-501">
            <div className="bg-gray-50 rounded-2xl p-8 md:p-10" data-id="element-502">
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-503">
                We Do the Job Right — Not Just Fast
              </h3>
              <p className="text-gray-600 leading-relaxed mb-8" data-id="element-504">
                A quality bathroom remodel often requires proper demo, careful
                prep, waterproofing, and precise installation — plus permits and
                inspections when required by your local area. We build timelines
                around craftsmanship and long-term durability, so your finished
                bathroom looks great and performs well for years to come.
              </p>

              <div className="bg-white border border-[#8BA888]/20 rounded-xl p-6 mb-6" data-id="element-505">
                <p className="text-sm font-semibold text-[#8BA888] uppercase tracking-wide mb-4" data-id="element-506">
                  When comparing remodelers, always ask:
                </p>
                <ul className="space-y-3" data-id="element-507">
                  {['Will permits be obtained if required by your local area?', 'What waterproofing and prep steps are included in the scope?', 'Who is doing the work — an accountable crew with one clear point of contact?'].map((q, i) => <li key={i} className="flex items-start gap-3" data-id="element-508">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#8BA888]/10 text-[#8BA888] font-bold text-sm mt-0.5" data-id="element-509">
                        {i + 1}
                      </span>
                      <span className="text-gray-700" data-id="element-510">{q}</span>
                    </li>)}
                </ul>
              </div>

              <p className="text-gray-700 font-medium" data-id="element-511">
                Our approach is simple:{' '}
                <span className="text-[#1E3A5F] font-bold" data-id="element-512">
                  quality first, clear communication, and a remodel you can
                  trust.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-gray-50" data-id="element-513">
        <div className="container mx-auto px-4 md:px-6" data-id="element-514">
          <BeforeAfterGallery data-id="element-515" />
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-20 bg-white" data-id="element-516">
        <div className="container mx-auto px-4 md:px-6" data-id="element-517">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-518">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-519">
              Our Services
            </h2>
            <p className="text-lg text-gray-600" data-id="element-520">
              Comprehensive bathroom remodeling solutions tailored to your needs
              and budget
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-521">
            {[{
            icon: Bath,
            title: 'Full Bathroom Remodeling',
            desc: "Complete bathroom transformations from design to installation, creating the space you've always wanted.",
            link: '/services/full-bathroom-remodeling'
          }, {
            icon: Droplets,
            title: 'Tub-to-Shower Conversions',
            desc: 'Replace your old tub with a modern, accessible walk-in shower for safety and convenience.',
            link: '/services/tub-to-shower-conversion'
          }, {
            icon: Grid3x3,
            title: 'Custom Tile Work',
            desc: 'Expert tile installation for floors, walls, showers, and backsplashes with precision craftsmanship.',
            link: '/services/custom-tile-work'
          }, {
            icon: BoxIcon,
            title: 'Vanity Installation',
            desc: 'Professional installation of new vanities to enhance storage and style in your bathroom.',
            link: '/services/vanity-installation'
          }, {
            icon: Accessibility,
            title: 'Accessibility Upgrades',
            desc: 'Safe, accessible modifications including grab bars, walk-in showers, and aging-in-place solutions.',
            link: '/services/accessibility-upgrades'
          }, {
            icon: Lightbulb,
            title: 'Flooring & Lighting',
            desc: 'Durable flooring options and lighting upgrades to complete your bathroom transformation.',
            link: '/services/flooring-and-lighting'
          }].map((service, index) => <Link key={index} to={service.link} className="group" data-id="element-522">
                <Card className="p-6 h-full hover:-translate-y-1 transition-all duration-300 border-t-4 border-t-[#8BA888] hover:shadow-xl" data-id="element-523">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#8BA888]/10 text-[#8BA888] group-hover:bg-[#8BA888] group-hover:text-white transition-colors" data-id="element-524">
                    <service.icon className="h-7 w-7" data-id="element-525" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#8BA888] transition-colors" data-id="element-526">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-527">
                    {service.desc}
                  </p>
                  <div className="flex items-center text-[#8BA888] font-medium text-sm group-hover:gap-2 transition-all" data-id="element-528">
                    Learn More
                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-529" />
                  </div>
                </Card>
              </Link>)}
          </div>

          <div className="text-center" data-id="element-530">
            <div className="inline-flex flex-col items-center gap-4 bg-gray-50 p-8 rounded-2xl" data-id="element-531">
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-532">
                Not Sure Which Service You Need?
              </h3>
              <p className="text-gray-600 max-w-2xl" data-id="element-533">
                Schedule a free consultation and we'll help you determine the
                best solution for your bathroom remodeling project.
              </p>
              <Link to="/contact" data-id="element-534">
                <Button size="lg" className="shadow-lg" data-id="element-535">
                  Get Your Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" data-id="element-536" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Serve Section */}
      <section className="relative py-20 overflow-hidden" data-id="element-537">
        {/* Background Map with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-538">
          <img src="https://cdn.magicpatterns.com/uploads/1wXBBoX6N5o1V8AuZct6Ru/map.png" alt="Northern Indiana service area map" className="w-full h-full object-cover" data-id="element-539" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/85 via-gray-900/80 to-gray-900/85" data-id="element-540"></div>
        </div>

        <div className="container relative z-10 mx-auto px-4 md:px-6" data-id="element-541">
          <div className="max-w-4xl mx-auto text-center" data-id="element-542">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-543">
              Proudly Serving Northern Indiana
            </h2>
            <p className="text-xl text-white/90 mb-12" data-id="element-544">
              Quality bathroom remodeling for homeowners across Northern
              Indiana.
            </p>

            {/* City Chips */}
            <div className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-545">
              {['Bristol', 'Chesterton', 'Crown Point', 'Elkhart', 'Fort Wayne', 'Goshen', 'Granger', 'La Porte', 'Merrillville', 'Michigan City', 'Middlebury', 'Mishawaka', 'Osceola', 'Plymouth', 'Portage', 'Rochester', 'South Bend', 'Valparaiso', 'Warsaw', 'Wakarusa', 'Northern Indiana'].map(city => <div key={city} className="px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium text-sm hover:bg-[#F5A623]/20 hover:border-[#F5A623]/40 transition-all duration-200 shadow-lg" data-id="element-546">
                  {city}
                </div>)}
            </div>

            <Link to="/contact" data-id="element-547">
              <Button size="lg" variant="secondary" className="shadow-xl" data-id="element-548">
                Request a Free Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Facebook Embed Section */}
      <section className="py-20 bg-white overflow-hidden" data-id="element-549">
        <div className="container mx-auto px-4 md:px-6" data-id="element-550">
          <div className="text-center mb-12" data-id="element-551">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-552">
              See Our Work & Updates
            </h2>
            <p className="text-lg text-gray-600" data-id="element-553">
              Follow us on Facebook for the latest projects and news.
            </p>
          </div>

          <div className="flex justify-center" data-id="element-554">
            <div className="w-full max-w-[500px] bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100" data-id="element-555">
              <iframe src="https://www.facebook.com/plugins/post.php?href=https%3A%2F%2Fwww.facebook.com%2Fluckyduckbath%2Fposts%2Fpfbid07Cfxg3UEJTd18eNmHcJaARwcGitDr66ezRFk9joeTRhvGNmYwcBxBPiYCKy4383pl&show_text=true&width=500" width="500" height="590" style={{
              border: 'none',
              overflow: 'hidden'
            }} scrolling="no" frameBorder="0" allowFullScreen={true} allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" className="w-full" data-id="element-556"></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#8BA888] text-white relative overflow-hidden" data-id="element-557">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-558"></div>
        <div className="container relative z-10 mx-auto px-4 md:px-6 text-center" data-id="element-559">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-560">
            Ready for a Bathroom You'll Love?
          </h2>
          <p className="text-xl text-white/95 mb-8 max-w-2xl mx-auto" data-id="element-561">
            Schedule your free consultation today. No pressure, just honest
            advice and a clear plan for your remodel.
          </p>
          <Link to="/contact" data-id="element-562">
            <Button size="lg" variant="primary" className="text-lg px-8 shadow-xl" data-id="element-563">
              Schedule Your Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>;
}